import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import Login from './Login';
import './Shop/shop.css';

// Guest Cart Service Functions
const GUEST_CART_KEY = 'guest_cart';

const getGuestCart = () => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch (error) {
    console.error('Error getting guest cart:', error);
    return [];
  }
};

const saveGuestCart = (cartItems) => {
  try {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
  } catch (error) {
    console.error('Error saving guest cart:', error);
  }
};

const addToGuestCart = (productId, variantId, quantity, productDetails) => {
  const guestCart = getGuestCart();
  const existingItemIndex = guestCart.findIndex(
    item => item.product_id === productId && item.variant_id === variantId
  );
  
  if (existingItemIndex !== -1) {
    guestCart[existingItemIndex].quantity += quantity;
  } else {
    guestCart.push({
      product_id: productId,
      variant_id: variantId,
      quantity: quantity,
      name: productDetails.name,
      price: productDetails.selling_price,
      image: productDetails.main_image,
      added_at: new Date().toISOString()
    });
  }
  
  saveGuestCart(guestCart);
  return guestCart;
};

const mergeGuestCartWithUserCart = async (customerId, token) => {
  const guestCart = getGuestCart();
  
  if (!guestCart || guestCart.length === 0) {
    return { success: true, message: 'No guest cart to merge' };
  }
  
  let mergedCount = 0;
  
  try {
    for (const item of guestCart) {
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/cart/', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          customer_id: String(customerId),
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity
        })
      });
      
      if (response.ok) {
        mergedCount++;
      }
    }
    
    // Clear guest cart after successful merge
    localStorage.removeItem(GUEST_CART_KEY);
    
    return { success: true, message: `Merged ${mergedCount} items from guest cart` };
  } catch (error) {
    console.error('Error merging guest cart:', error);
    return { success: false, message: 'Failed to merge guest cart' };
  }
};

const Wishlist = () => {
  const navigate = useNavigate();
  const [wishlistData, setWishlistData] = useState({ wishlist_id: '', items: [], item_count: 0 });
  const [quantities, setQuantities] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [error, setError] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState('');
  const [selectedVariants, setSelectedVariants] = useState({});
  const [productDetails, setProductDetails] = useState({});
  
  const [isFetchingWishlist, setIsFetchingWishlist] = useState(false);
  const [isFetchingProductDetails, setIsFetchingProductDetails] = useState({});

  const getCustomerId = () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsed = JSON.parse(userData);
        if (parsed.id) return parsed.id;
        if (parsed.customer_id) return parsed.customer_id;
      }
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const payload = JSON.parse(window.atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          return payload.user_id || payload.id || payload.customer_id || payload.sub || null;
        } catch (e) {}
      }
      return null;
    } catch (e) { return null; }
  };

  const fetchProductDetails = async (productId) => {
    if (productDetails[productId]) {
      return productDetails[productId];
    }
    
    if (isFetchingProductDetails[productId]) {
      return null;
    }
    
    setIsFetchingProductDetails(prev => ({ ...prev, [productId]: true }));
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`https://evercorenutrition.com/ev_admin/api/products/${productId}/`, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const product = await response.json();
        setProductDetails(prev => ({ ...prev, [productId]: product }));
        return product;
      }
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setIsFetchingProductDetails(prev => ({ ...prev, [productId]: false }));
    }
    return null;
  };

  const getDefaultVariant = (product) => {
    if (product.variants && product.variants.length > 0) {
      return product.variants[0];
    }
    return null;
  };

  const getSelectedVariant = (productId) => {
    const product = productDetails[productId] || wishlistData.items.find(p => p.id === productId);
    
    if (!product) return null;
    
    if (!product.variants || product.variants.length === 0) {
      return null;
    }
    
    const selectedVariantId = selectedVariants[productId];
    
    if (selectedVariantId) {
      const variant = product.variants.find(v => v.id === selectedVariantId);
      if (variant) return variant;
    }
    
    return product.variants[0];
  };

  const getVariantIdForCart = (productId) => {
    const variant = getSelectedVariant(productId);
    return variant?.id || null;
  };

  const getProductImageUrl = (product) => {
    const img = product.main_image || product.image;
    if (!img) return null;
    return img.startsWith('http') ? img : `https://evercorenutrition.com${img}`;
  };

  const isInStock = (productId) => {
    const product = productDetails[productId] || wishlistData.items.find(p => p.id === productId);
    
    if (!product) return false;
    
    if (product.qty !== undefined && product.qty !== null) {
      return product.qty > 0;
    }
    
    const selectedVariant = getSelectedVariant(productId);
    if (selectedVariant) {
      if (selectedVariant.qty !== undefined && selectedVariant.qty !== null) {
        return selectedVariant.qty > 0;
      }
      if (selectedVariant.stock_status !== undefined) {
        return selectedVariant.stock_status !== "Stockout" && selectedVariant.stock_status !== "out_of_stock";
      }
    }
    
    return false;
  };

  const getQuantity = (productId) => quantities[productId] || 1;
  const increaseQuantity = (productId) => setQuantities(prev => ({ ...prev, [productId]: (prev[productId] || 1) + 1 }));
  const decreaseQuantity = (productId) => setQuantities(prev => ({ ...prev, [productId]: Math.max(1, (prev[productId] || 1) - 1) }));

  const closeWarning = () => { setShowWarning(false); setWarningMessage(''); };

  const showToast = (msg, isSuccess = true) => {
    setWarningMessage(msg);
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 2000);
  };

  // Modified handleLoginSuccess to merge guest cart
  const handleLoginSuccess = async () => {
    setShowLoginModal(false);
    
    // Fetch wishlist first
    await fetchWishlist();
    
    // Merge guest cart with user cart
    const token = localStorage.getItem('authToken');
    const custId = getCustomerId();
    
    if (token && custId) {
      const mergeResult = await mergeGuestCartWithUserCart(custId, token);
      if (mergeResult.success && mergeResult.message !== 'No guest cart to merge') {
        showToast(mergeResult.message);
        window.dispatchEvent(new Event('cartUpdated'));
      }
    }
    
    window.dispatchEvent(new Event('wishlistUpdated'));
    showToast('Login successful!');
  };

  const fetchWishlist = async () => {
    if (isFetchingWishlist) return;
    
    const token = localStorage.getItem('authToken');
    const custId = getCustomerId();
    if (!token) { setLoading(false); return; }
    if (!custId) { setLoading(false); setError('Could not identify customer. Please login again.'); return; }
    
    setIsFetchingWishlist(true);
    setCustomerId(custId);
    
    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${custId}`,
        { method: 'GET', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.wishlist) {
          const products = data.wishlist.products || [];
          setWishlistData({
            wishlist_id: data.wishlist.wishlist_id || '',
            items: products,
            item_count: data.wishlist.total_items || products.length
          });
          const qtyInit = {};
          products.forEach(p => { qtyInit[p.id] = 1; });
          setQuantities(qtyInit);
          
          products.forEach(product => {
            fetchProductDetails(product.id);
          });
        }
      } else if (response.status === 401) {
        localStorage.removeItem('authToken');
        setError('Session expired. Please login again.');
      } else {
        setError('Failed to load wishlist');
      }
    } catch (err) {
      setError('Failed to load wishlist');
    } finally {
      setLoading(false);
      setIsFetchingWishlist(false);
    }
  };

  useEffect(() => {
    if (wishlistData.items.length > 0 && Object.keys(productDetails).length > 0) {
      const defaultVariants = {};
      wishlistData.items.forEach(item => {
        const product = productDetails[item.id];
        if (product?.variants && product.variants.length > 0) {
          defaultVariants[item.id] = product.variants[0].id;
        }
      });
      setSelectedVariants(defaultVariants);
    }
  }, [productDetails, wishlistData.items]);

  useEffect(() => { 
    fetchWishlist(); 
  }, []);

  const removeFromWishlist = async (productId) => {
    const token = localStorage.getItem('authToken');
    const custId = customerId || getCustomerId();
    if (!token || !custId) { showToast('Please login to manage your wishlist'); return; }
    setActionLoading(prev => ({ ...prev, [productId]: 'removing' }));
    try {
      const response = await fetch(
        `https://evercorenutrition.com/ev_admin/api/wishlist/?customer_id=${custId}&product_id=${productId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      if (response.status === 401) { localStorage.removeItem('authToken'); showToast('Session expired. Please login again.'); return; }
      if (!response.ok) { const e = await response.json().catch(() => ({})); throw new Error(e.message || e.error || 'Failed to remove item'); }
      setWishlistData(prev => ({ ...prev, items: prev.items.filter(item => item.id !== productId), item_count: prev.item_count - 1 }));
      showToast('Item removed from wishlist');
      window.dispatchEvent(new Event('wishlistUpdated'));
    } catch (err) {
      showToast(err.message || 'Error removing item');
    } finally {
      setActionLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  // Modified addToCart with guest cart support
  const addToCart = async (product) => {
    const productId = product.id;
    const inStock = isInStock(productId);
    
    if (!inStock) {
      showToast('This product is currently out of stock');
      return;
    }
    
    const token = localStorage.getItem('authToken');
    const custId = customerId || getCustomerId();
    const variantId = getVariantIdForCart(productId);
    const qty = getQuantity(productId);
    
    // If not logged in, save to guest cart
    if (!token || !custId) {
      const fullProduct = productDetails[productId] || product;
      const productDetailsForCart = {
        name: fullProduct.name,
        selling_price: fullProduct.selling_price,
        main_image: fullProduct.main_image
      };
      
      addToGuestCart(productId, variantId, qty, productDetailsForCart);
      showToast('Item added to cart! (Saved locally)', true);
      window.dispatchEvent(new Event('cartUpdated'));
      return;
    }
    
    if (!variantId) {
      showToast('Unable to add to cart. Please try again.');
      return;
    }
    
    setActionLoading(prev => ({ ...prev, [productId]: 'adding' }));
    try {
      const response = await fetch('https://evercorenutrition.com/ev_admin/api/cart/', {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`, 
          'Content-Type': 'application/json' 
        },
        body: JSON.stringify({ 
          customer_id: String(custId), 
          product_id: productId, 
          variant_id: variantId,
          quantity: qty 
        }),
      });
      if (response.ok) {
        showToast('Item added to cart!', true);
        window.dispatchEvent(new Event('cartUpdated'));
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.message || errorData.error || 'Failed to add to cart.');
      }
    } catch (err) {
      showToast('Network error. Please try again.');
    } finally {
      setActionLoading(prev => ({ ...prev, [productId]: false }));
    }
  };

  const shareProduct = (product) => {
    const url = window.location.origin + '/product/' + product.id;
    if (navigator.share) {
      navigator.share({ title: product.name, text: 'Check out this product!', url });
    } else {
      navigator.clipboard.writeText(url);
      showToast('Product link copied!', true);
    }
  };

  // Function to render star rating (matching shop page)
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    let stars = '';
    
    for (let i = 0; i < fullStars; i++) {
      stars += '★';
    }
    
    if (hasHalfStar) {
      stars += '½';
    }
    
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars += '☆';
    }
    
    return stars;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      {showLoginModal && <Login setOpen={setShowLoginModal} onLoginSuccess={handleLoginSuccess} />}

      {showWarning && (
        <div style={{
          position: 'fixed', top: '20px', right: '20px',
          backgroundColor: warningMessage.includes('added') || warningMessage.includes('removed') || warningMessage.includes('copied') || warningMessage.includes('Saved') ? '#4CAF50' :
            (warningMessage.includes('out of stock') ? '#ff9800' : '#ff4444'),
          color: 'white', padding: '15px 25px', borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: '10px', animation: 'slideIn 0.3s ease'
        }}>
          <i className={`fa-${warningMessage.includes('added') || warningMessage.includes('removed') || warningMessage.includes('copied') || warningMessage.includes('Saved') ? 'regular fa-circle-check' : 'solid fa-circle-exclamation'}`}></i>
          <span>{warningMessage}</span>
          <button onClick={closeWarning} style={{ background: 'none', border: 'none', color: 'white', marginLeft: '10px', cursor: 'pointer', fontSize: '18px' }}>×</button>
        </div>
      )}

      <section className="breadcrumb-section pt-0">
        <div className="container-fluid-lg">
          <div className="row">
            <div className="col-12">
              <div className="breadcrumb-contain">
                <h2>My Wishlist {wishlistData.item_count > 0 && `(${wishlistData.item_count} items)`}</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none' }}>
                        <i className="fa-solid fa-house"></i>
                      </button>
                    </li>
                    <li className="breadcrumb-item active">Wishlist</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-b-space shop-section">
        <div className="container-fluid-lg">

          {error && (
            <div className="alert alert-danger alert-dismissible fade show" role="alert">
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {!localStorage.getItem('authToken') && (
            <div className="text-center py-5">
              <i className="fa-regular fa-heart" style={{ fontSize: '48px', color: '#ccc', marginBottom: '20px', display: 'block' }}></i>
              <h4>Please login to view your wishlist</h4>
              <button onClick={() => setShowLoginModal(true)} style={{ backgroundColor: '#000', color: 'white', border: 'none', padding: '10px 30px', borderRadius: '4px', marginTop: '15px', cursor: 'pointer' }}>
                Login
              </button>
            </div>
          )}

          {localStorage.getItem('authToken') && wishlistData.items.length === 0 && (
            <div className="text-center py-5">
              <i className="fa-regular fa-heart" style={{ fontSize: '48px', color: '#ccc', marginBottom: '20px', display: 'block' }}></i>
              <h4>Your wishlist is empty</h4>
              <p>Save your favorite items here</p>
              <button onClick={() => navigate('/shop')} style={{ backgroundColor: '#000', color: 'white', border: 'none', padding: '10px 30px', borderRadius: '4px', marginTop: '15px', cursor: 'pointer' }}>
                Continue Shopping
              </button>
            </div>
          )}

          {localStorage.getItem('authToken') && wishlistData.items.length > 0 && (
            <div className="row" style={{ margin: 0 }}>
              <div className="col-12" style={{ padding: 0 }}>
                <div style={{ display: 'grid', gap: '20px', gridTemplateColumns: '1fr', width: '100%', margin: 0, padding: 0 }}>

                  {wishlistData.items.map((product) => {
                    const productId = product.id;
                    const isLoading = actionLoading[productId];
                    const inStock = isInStock(productId);
                    const fullProduct = productDetails[productId] || product;
                    const selectedVariant = getSelectedVariant(productId);
                    const variantPrice = selectedVariant?.selling_price || fullProduct.selling_price || product.selling_price;
                    const variantOriginalPrice = selectedVariant?.price || fullProduct.price || product.price;
                    const variantDiscount = selectedVariant?.discount || fullProduct.discount || product.discount;
                    const averageRating = fullProduct.average_rating || 0;
                    const totalReviews = fullProduct.total_reviews || 0;

                    return (
                      <div key={productId} style={{ width: '100%', padding: 0, margin: 0, boxShadow: "1px 2px 15px #868585", borderRadius: "10px" }}>
                        <div
                          style={{
                            borderRadius: '12px', overflow: 'hidden',
                            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                            backgroundColor: '#f0f0f0', display: 'flex',
                            flexDirection: 'column', height: '100%',
                            cursor: 'pointer', position: 'relative',
                            opacity: inStock ? 1 : 0.8
                          }}
                          onMouseEnter={(e) => {
                            if (window.innerWidth > 768 && inStock) {
                              e.currentTarget.style.transform = 'translateY(-5px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                          }}
                        >
                          {/* Stock Status Badge */}
                          {!inStock && (
                            <div style={{
                              position: 'absolute', top: '10px', left: '10px',
                              backgroundColor: '#ff4444', color: 'white',
                              padding: '4px 12px', borderRadius: '20px',
                              fontSize: '12px', fontWeight: '600', zIndex: 3,
                              boxShadow: '0 2px 8px rgba(255,68,68,0.3)'
                            }}>
                              <i className="fa-solid fa-clock" style={{ marginRight: '5px' }}></i>
                              Out of Stock
                            </div>
                          )}

                          {/* Product Image */}
                          <div style={{ 
                            position: 'relative', 
                            width: '100%',
                            aspectRatio: '1/1',
                            overflow: 'hidden', 
                            backgroundColor: '#f0f0f0' 
                          }}>
                            <a href={`/product/${productId}`} style={{ display: 'block', width: '100%', height: '100%' }}>
                              {getProductImageUrl(product) && (
                                <img
                                  src={getProductImageUrl(product)}
                                  alt={product.name}
                                  style={{
                                    width: '100%', height: '100%', objectFit: 'cover',
                                    padding: '0', transition: 'transform 0.3s ease',
                                    filter: inStock ? 'none' : 'grayscale(50%)'
                                  }}
                                  onMouseEnter={(e) => { if (window.innerWidth > 768 && inStock) e.currentTarget.style.transform = 'scale(1.05)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                />
                              )}
                            </a>

                            {/* Remove from Wishlist Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromWishlist(productId);
                              }}
                              disabled={!!isLoading}
                              style={{
                                position: 'absolute', top: '10px', right: '50px',
                                width: '35px', height: '35px', borderRadius: '50%',
                                border: 'none', backgroundColor: 'white',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                cursor: isLoading ? 'wait' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                zIndex: 2, opacity: isLoading ? 0.7 : 1
                              }}
                            >
                              {isLoading === 'removing' ? (
                                <span className="spinner-border spinner-border-sm" role="status" style={{ width: '16px', height: '16px' }}></span>
                              ) : (
                                <i className="fa-solid fa-heart" style={{ color: '#ff4444', fontSize: '16px' }}></i>
                              )}
                            </button>

                            {/* Share Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                shareProduct(product);
                              }}
                              style={{
                                position: 'absolute', top: '10px', right: '10px',
                                width: '35px', height: '35px', borderRadius: '50%',
                                border: 'none', backgroundColor: 'white',
                                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                cursor: 'pointer', display: 'flex',
                                alignItems: 'center', justifyContent: 'center', zIndex: 2
                              }}
                            >
                              <i className="fa fa-share-alt" style={{ color: '#333' }}></i>
                            </button>
                          </div>

                          {/* Content Section */}
                          <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {/* Product Name */}
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: '0 0 8px 0', color: '#333', lineHeight: '1.4' }}>
                              <a href={`/product/${productId}`} style={{ color: '#333', textDecoration: 'none' }}>
                                {product.name}
                              </a>
                            </h3>

                            {/* Rating */}
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' }}>
                              <div style={{ color: '#ffc107', fontSize: '1rem', letterSpacing: '2px', marginRight: '5px' }}>
                                {renderStars(averageRating)}
                              </div>
                              <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '5px' }}>
                                ({averageRating > 0 ? averageRating.toFixed(1) : '0.0'})
                              </span>
                              <span style={{ fontSize: '0.85rem', color: '#999', marginLeft: '5px' }}>
                                ({totalReviews} {totalReviews === 1 ? 'review' : 'reviews'})
                              </span>
                            </div>

                            {/* Pack Size / Weight */}
                            {fullProduct.weight_value && (
                              <p style={{ fontSize: '0.9rem', color: '#666', margin: '0 0 10px 0' }}>
                                {fullProduct.weight_value} {fullProduct.weight_unit}
                              </p>
                            )}

                            {/* Price Section - Matching Shop Page */}
                            <div style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
                              <span style={{ fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
                                ₹{parseFloat(variantPrice || 0).toLocaleString('en-IN')}
                              </span>
                              {variantOriginalPrice > variantPrice && (
                                <span style={{ fontSize: '0.9rem', color: '#999', textDecoration: 'line-through', marginLeft: '8px' }}>
                                  ₹{parseFloat(variantOriginalPrice || 0).toLocaleString('en-IN')}
                                </span>
                              )}
                              {variantDiscount > 0 && (
                                <span style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '3px',
                                  fontSize: '0.75rem',
                                  color: '#fff',
                                  backgroundColor: '#e53935',
                                  padding: '4px 10px',
                                  borderRadius: '20px',
                                  marginLeft: '4px',
                                  fontWeight: '700',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {parseFloat(variantDiscount).toFixed(1)}% OFF
                                </span>
                              )}
                            </div>

                            {/* Add to Cart Button - Matching Shop Page */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(product);
                              }}
                              disabled={!!isLoading || !inStock}
                              style={{ 
                                width: '100%',
                                padding: '10px 12px', 
                                backgroundColor: !inStock ? '#999' : '#000', 
                                color: 'white', 
                                border: 'none', 
                                borderRadius: '6px', 
                                fontSize: '0.9rem', 
                                fontWeight: '600', 
                                cursor: (!inStock || isLoading) ? 'not-allowed' : 'pointer', 
                                transition: 'background-color 0.2s ease',
                                opacity: !inStock ? 0.7 : 1,
                                marginTop: 'auto'
                              }}
                              onMouseEnter={(e) => { 
                                if (window.innerWidth > 768 && !isLoading && inStock) {
                                  e.currentTarget.style.backgroundColor = '#333'; 
                                }
                              }}
                              onMouseLeave={(e) => { 
                                if (inStock) {
                                  e.currentTarget.style.backgroundColor = '#000'; 
                                }
                              }}
                            >
                              {!inStock ? 'Out of Stock' : isLoading === 'adding' ? 'Adding...' : 'Add to Cart'}
                            </button>

                            {!inStock && (
                              <p style={{ fontSize: '0.75rem', color: '#ff4444', margin: '8px 0 0', textAlign: 'center', fontWeight: '500' }}>
                                <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '4px' }}></i>
                                Currently unavailable
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <style>{`
        @media (min-width: 768px) {
          div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 1200px) {
          div[style*="display: grid"][style*="gap: 20px"] {
            grid-template-columns: repeat(4, 1fr) !important;
          }
        }
        .container-fluid-lg { padding-left: 15px !important; padding-right: 15px !important; }
        @media (min-width: 768px) { .container-fluid-lg { padding-left: 20px !important; padding-right: 20px !important; } }
        @media (min-width: 1200px) { .container-fluid-lg { padding-left: 30px !important; padding-right: 30px !important; } }
        .row { margin-left: 0 !important; margin-right: 0 !important; }
        .col-12 { padding-left: 0 !important; padding-right: 0 !important; }
        .breadcrumb-section { background-color: #f8f9fa; padding: 20px 0; margin-bottom: 30px; }
        .breadcrumb-contain { display: flex; justify-content: space-between; align-items: center; }
        .breadcrumb-contain h2 { margin: 0; font-size: 24px; font-weight: 600; }
        .breadcrumb { background: transparent; padding: 0; margin: 0; }
        .breadcrumb-item button { color: #007bff; cursor: pointer; }
        .breadcrumb-item.active { color: #6c757d; }
        @keyframes slideIn {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>

      <Footer />
    </>
  );
};

export default Wishlist;